import type { AdSlot } from '../schemas.js';

export type { AdSlot };
export const AD_SLOTS: readonly AdSlot[] = ['rescue', 'doubleTips', 'fastRestock', 'dailySpin', 'doubleOffline', 'freeGems', 'booster'];

export interface AdsInitOptions {
  /** consent to personalised ads (ConsentStore) and not a COPPA account */
  personalized: boolean;
  /** COPPA mode (SY-02.1): contextual ads only, child-directed treatment tag */
  childDirected: boolean;
}

/** `nofill`/`failed` are ad-load failures (MO-01.2: log them with the network); `dismissed` is the player closing early. */
export type AdError = 'nofill' | 'failed' | 'dismissed';

export interface RewardedResult {
  /** true only after the network's completion callback (MO-01.1) */
  completed: boolean;
  network: string;
  /** opaque network proof forwarded to POST /v1/ads/reward/complete */
  proof?: string;
  error?: AdError;
  detail?: string;
}

/** Per view: what POST /v1/ads/reward/start answered about it. */
export interface AdViewOptions {
  /** AdStartResponse.personalized: false makes this request non-personalised (AdMob `npa`) */
  personalized?: boolean;
}

export interface AdsProvider {
  readonly network: string;
  init(opts: AdsInitOptions): Promise<void>;
  isRewardedReady(slot: AdSlot): Promise<boolean>;
  /** `nonce` comes from POST /v1/ads/reward/start and is bound to the view (AdMob SSV custom_data); `view` is the rest of that answer. */
  showRewarded(slot: AdSlot, nonce: string, view?: AdViewOptions): Promise<RewardedResult>;
  showInterstitial(): Promise<{ shown: boolean }>;
}

/** Selected by `ads=none` (Discord Activity, ads switched off). Every button falls back (see rewardedOffer). */
export class NoAdsProvider implements AdsProvider {
  readonly network = 'none';
  async init(): Promise<void> {}
  async isRewardedReady(_slot: AdSlot): Promise<boolean> {
    return false;
  }
  async showRewarded(_slot: AdSlot, _nonce: string): Promise<RewardedResult> {
    return { completed: false, network: this.network, error: 'nofill' };
  }
  async showInterstitial(): Promise<{ shown: boolean }> {
    return { shown: false };
  }
}

/** Personalised only with consent and never for a child account. */
export const nonPersonalized = (o: AdsInitOptions): boolean => !o.personalized || o.childDirected;
